import { useState, useMemo, useCallback } from "react";
import TableHeader from "./table-header";
import TableBody from "./table-body";
import { GenericTableProps, SortState } from "./constants";
import { getNextSortDirection, sortData } from "./helpers";
import Pagination from "./pagination";

const GenericTable: React.FC<GenericTableProps> = ({
  columns,
  data,
  loading = false,
  currentPage = 1,
  totalPages = 1,
  // rowsPerPage = 10,
  onPageChange,
  actions,
  // External sorting props
  sortState,
  onSort,
  enableInternalSorting = false,
  idColumnIcon,
}) => {
  // Internal sort state - only used when enableInternalSorting is true
  const [internalSortState, setInternalSortState] = useState<SortState>({
    column: null,
    direction: null,
  });

  // Use external sort state if provided, otherwise use internal
  const currentSortState = sortState || internalSortState;
  const setCurrentSortState = onSort || setInternalSortState;

  // Memoized sorted data - only sort the current page data
  const sortedData = useMemo(() => {
    // If external sorting is enabled, don't sort locally
    if (sortState && !enableInternalSorting) {
      return data;
    }
    return sortData(data, columns, currentSortState);
  }, [data, columns, currentSortState, sortState, enableInternalSorting]);

  // Memoized sort handler - prevents unnecessary re-renders
  const handleSort = useCallback(
    (columnKey: string) => {
      const column = columns.find((col) => col.key === columnKey);
      if (!column || !column.sortable) return;

      // For external sorting, we want to toggle between asc/desc
      if (sortState && !enableInternalSorting && column?.sortKey) {
        const actualSortKey = column.sortKey;

        // Check if we're clicking on the same column that's already sorted
        if (currentSortState.column === actualSortKey) {
          // Toggle direction: asc → desc, desc → asc
          const newDirection =
            currentSortState.direction === "asc" ? "desc" : "asc";
          setCurrentSortState({
            column: actualSortKey,
            direction: newDirection,
          });
        } else {
          // New column: start with asc
          setCurrentSortState({
            column: actualSortKey,
            direction: "asc",
          });
        }
      } else {
        // Internal sorting logic (existing behavior) - for columns without sortKey
        const newSortState = getNextSortDirection(
          currentSortState.column,
          currentSortState.direction,
          columnKey
        );
        setInternalSortState(newSortState);
      }
    },
    [
      columns,
      currentSortState,
      setCurrentSortState,
      sortState,
      enableInternalSorting,
    ]
  );

  // Memoized page handlers
  const handlePrevPage = useCallback(() => {
    onPageChange?.(currentPage - 1);
  }, [onPageChange, currentPage]);

  const handleNextPage = useCallback(() => {
    onPageChange?.(currentPage + 1);
  }, [onPageChange, currentPage]);
  const LoadingOverlay: React.FC = () => (
    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10">
      <div className="flex flex-col items-center gap-2">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    </div>
  );
  return (
    <div className="bg-white border-t border-gray-200 overflow-hidden relative rounded-b-lg ">
      <div
        className={`overflow-x-auto  overflow-y-auto 
        `}
      >
        <table className="w-full">
          <TableHeader
            columns={columns}
            onSort={handleSort}
            actions={!!actions}
            idColumnIcon={idColumnIcon}
          />
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={columns.length} className="h-[300px]">
                  <div className="flex items-center justify-center">
                    <p className="text-gray-500 text-center">Loading...</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : data?.length > 0 ? (
            <TableBody data={sortedData} columns={columns} actions={actions} />
          ) : (
            <tbody>
              <tr>
                <td colSpan={columns.length} className="h-[300px]">
                  <div className="flex items-center justify-center">
                    <p className="text-gray-500 text-center">No Data found</p>
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onPageChange={onPageChange}
      />
      {/* Loading overlay */}
      {loading && <LoadingOverlay />}
    </div>
  );
};

export default GenericTable;
